import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './MenuItem.module.css';
import { motion, useAnimationControls } from 'framer-motion';

const PAINT_OUT_BAR_IN_S = 0.25;
const PAINT_OUT_BAR_OUT_S = 0.25;
const PAINT_OUT_FADE_MS = 280;
const PRESS_HIGHLIGHT_S = 0.22;
const HOVER_BAR_DURATION_S = 0.3;
const HOVER_BAR_OUT_DURATION_S = 0.5;
/** Menu row paint-in (desktop + mobile overlay): bar wipe + stagger between rows. */
const MENU_INTRO_BAR_DURATION_S = 0.22;
const MENU_INTRO_ROW_STAGGER_S = 0.065;

interface MenuItemProps {
    index: number;
    title: string;
    onClick: () => void;
    isSelected?: boolean;
    /** Increment to play a coordinated paint-out (mobile overlay). */
    paintOutSignal?: number;
    /** Delay before this row’s paint-out starts (stagger). */
    paintOutExitDelayMs?: number;
    paintOutGeneration?: number;
    /** Mobile overlay: bar wipe vs whole-row fade when closing. */
    paintOutVariant?: 'wipe' | 'fade' | 'wipeOverText';
    onPaintOutComplete?: (generation: number) => void;
    /** When true, skip pointer hover bar wipes (mobile overlay / touch). */
    disableHover?: boolean;
}

const MenuItem: React.FC<MenuItemProps> = ({
    index,
    title,
    onClick,
    isSelected = false,
    paintOutSignal = 0,
    paintOutExitDelayMs = 0,
    paintOutGeneration = 0,
    paintOutVariant = 'wipe',
    onPaintOutComplete,
    disableHover = false,
}) => {
    const bgControls = useAnimationControls();
    const pressHighlightControls = useAnimationControls();
    const wipeOverControls = useAnimationControls();
    const introCompleteRef = useRef(false);
    const [textOpacity, setTextOpacity] = useState(0);
    const [fadePaintOutActive, setFadePaintOutActive] = useState(false);
    const [textPressWhite, setTextPressWhite] = useState(false);
    /** Ignore stale paintOutSignal on remount (parent tick persists after overlay unmount). */
    const lastPaintOutSignalRef = useRef(paintOutSignal);
    const paintOutRunningRef = useRef(false);
    const pressHighlightRunningRef = useRef(false);
    const isMobileMenuRow = Boolean(disableHover && onPaintOutComplete);

    useEffect(() => {
        let cancelled = false;

        const runIntro = async () => {
            pressHighlightControls.set({ scaleX: 0, transformOrigin: 'left center' });
            wipeOverControls.set({ scaleX: 0, transformOrigin: 'left center' });
            await bgControls.start({
                scaleX: 1,
                transformOrigin: 'left center',
                transition: {
                    duration: MENU_INTRO_BAR_DURATION_S,
                    delay: index * MENU_INTRO_ROW_STAGGER_S,
                    ease: 'easeInOut',
                },
            });
            if (cancelled) return;
            setTextOpacity(1);
            if (cancelled) return;
            await bgControls.start({
                scaleX: 0,
                transformOrigin: 'right center',
                transition: { duration: MENU_INTRO_BAR_DURATION_S, ease: 'easeInOut' },
            });
            if (!cancelled) introCompleteRef.current = true;
        };

        void runIntro();

        return () => {
            cancelled = true;
        };
    }, [bgControls, index, pressHighlightControls, wipeOverControls]);

    useEffect(() => {
        if (!onPaintOutComplete) return;
        if (paintOutSignal <= 0 || paintOutSignal <= lastPaintOutSignalRef.current) return;
        lastPaintOutSignalRef.current = paintOutSignal;
        let cancelled = false;
        paintOutRunningRef.current = true;

        const resetPressLayers = () => {
            pressHighlightControls.stop();
            pressHighlightControls.set({ scaleX: 0, transformOrigin: 'left center' });
            wipeOverControls.stop();
            wipeOverControls.set({ scaleX: 0, transformOrigin: 'left center' });
            setTextPressWhite(false);
        };

        const runPaintOut = async () => {
            introCompleteRef.current = false;
            if (paintOutExitDelayMs > 0) {
                await new Promise<void>((resolve) => setTimeout(resolve, paintOutExitDelayMs));
            }
            if (cancelled) return;

            setFadePaintOutActive(false);

            if (paintOutVariant === 'fade') {
                bgControls.stop();
                bgControls.set({ scaleX: 0, transformOrigin: 'left center' });
                resetPressLayers();
                setFadePaintOutActive(true);
                await new Promise<void>((resolve) => setTimeout(resolve, PAINT_OUT_FADE_MS));
                if (!cancelled && onPaintOutComplete) {
                    onPaintOutComplete(paintOutGeneration);
                }
                paintOutRunningRef.current = false;
                return;
            }

            if (paintOutVariant === 'wipeOverText') {
                bgControls.stop();
                bgControls.set({ scaleX: 0, transformOrigin: 'left center' });
                setTextOpacity(1);
                setTextPressWhite(true);
                wipeOverControls.set({ scaleX: 0, transformOrigin: 'left center' });
                await wipeOverControls.start({
                    scaleX: 1,
                    transformOrigin: 'left center',
                    transition: { duration: PAINT_OUT_BAR_IN_S, ease: 'easeInOut' },
                });
                if (cancelled) return;
                setTextOpacity(0);
                resetPressLayers();
                await wipeOverControls.start({
                    scaleX: 0,
                    transformOrigin: 'right center',
                    transition: { duration: PAINT_OUT_BAR_OUT_S, ease: 'easeInOut' },
                });
                if (!cancelled && onPaintOutComplete) {
                    onPaintOutComplete(paintOutGeneration);
                }
                paintOutRunningRef.current = false;
                return;
            }

            bgControls.stop();
            resetPressLayers();
            setTextOpacity(0);
            bgControls.set({ scaleX: 0, transformOrigin: 'left center' });
            await bgControls.start({
                scaleX: 1,
                transformOrigin: 'left center',
                transition: { duration: PAINT_OUT_BAR_IN_S, ease: 'easeInOut' },
            });
            if (cancelled) return;
            await bgControls.start({
                scaleX: 0,
                transformOrigin: 'right center',
                transition: { duration: PAINT_OUT_BAR_OUT_S, ease: 'easeInOut' },
            });
            if (!cancelled && onPaintOutComplete) {
                onPaintOutComplete(paintOutGeneration);
            }
            paintOutRunningRef.current = false;
        };

        void runPaintOut();

        return () => {
            cancelled = true;
            bgControls.stop();
            pressHighlightControls.stop();
            wipeOverControls.stop();
            setFadePaintOutActive(false);
            paintOutRunningRef.current = false;
        };
    }, [
        bgControls,
        onPaintOutComplete,
        paintOutExitDelayMs,
        paintOutGeneration,
        paintOutSignal,
        paintOutVariant,
        pressHighlightControls,
        wipeOverControls,
    ]);

    const abortPressHighlight = useCallback(() => {
        pressHighlightRunningRef.current = false;
        pressHighlightControls.stop();
        pressHighlightControls.set({ scaleX: 0, transformOrigin: 'left center' });
        setTextPressWhite(false);
    }, [pressHighlightControls]);

    const runPressHighlight = useCallback(async () => {
        if (!isMobileMenuRow || paintOutRunningRef.current) return;
        pressHighlightRunningRef.current = true;
        bgControls.stop();
        bgControls.set({ scaleX: 0, transformOrigin: 'left center' });
        pressHighlightControls.stop();
        pressHighlightControls.set({ scaleX: 0, transformOrigin: 'left center' });
        setTextOpacity(1);
        setTextPressWhite(true);
        await pressHighlightControls.start({
            scaleX: 1,
            transformOrigin: 'left center',
            transition: { duration: PRESS_HIGHLIGHT_S, ease: 'easeInOut' },
        });
        if (pressHighlightRunningRef.current) {
            pressHighlightRunningRef.current = false;
        }
    }, [bgControls, isMobileMenuRow, pressHighlightControls]);

    const onPointerDown = useCallback(
        (event: React.PointerEvent) => {
            if (!isMobileMenuRow || paintOutRunningRef.current) return;
            if (event.pointerType === 'mouse' && event.button !== 0) return;
            try {
                event.currentTarget.setPointerCapture(event.pointerId);
            } catch {
                /* ignore */
            }
            void runPressHighlight();
        },
        [isMobileMenuRow, runPressHighlight],
    );

    const onPointerUp = useCallback(
        (event: React.PointerEvent) => {
            if (!isMobileMenuRow) return;
            try {
                if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                    event.currentTarget.releasePointerCapture(event.pointerId);
                }
            } catch {
                /* ignore */
            }
            if (event.pointerType === 'mouse' && event.button !== 0) return;
            if (paintOutRunningRef.current) return;
            onClick();
        },
        [isMobileMenuRow, onClick],
    );

    const onPointerCancel = useCallback(
        (event: React.PointerEvent) => {
            if (!isMobileMenuRow) return;
            try {
                if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                    event.currentTarget.releasePointerCapture(event.pointerId);
                }
            } catch {
                /* ignore */
            }
            abortPressHighlight();
        },
        [abortPressHighlight, isMobileMenuRow],
    );

    const onKeyActivate = useCallback(
        (event: React.KeyboardEvent) => {
            if (!isMobileMenuRow) return;
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            if (paintOutRunningRef.current) return;
            onClick();
        },
        [isMobileMenuRow, onClick],
    );

    const onPointerEnter = useCallback(() => {
        if (disableHover) return;
        if (!introCompleteRef.current) return;
        void bgControls.start({
            scaleX: 1,
            transformOrigin: 'left center',
            transition: { duration: HOVER_BAR_DURATION_S, ease: 'easeInOut' },
        });
    }, [bgControls, disableHover]);

    const onPointerLeave = useCallback(() => {
        if (disableHover) return;
        if (!introCompleteRef.current) return;
        void (async () => {
            bgControls.stop();
            bgControls.set({
                scaleX: 1,
                transformOrigin: 'left center',
            });
            await bgControls.start({
                scaleX: 0,
                transformOrigin: 'right center',
                transition: { duration: HOVER_BAR_OUT_DURATION_S, ease: 'easeInOut' },
            });
        })();
    }, [bgControls, disableHover]);

    return (
        <div
            className={`${styles.container} ${fadePaintOutActive ? styles.fadePaintOut : ''}`}
            role={isMobileMenuRow ? 'menuitem' : undefined}
            tabIndex={isMobileMenuRow ? 0 : undefined}
            onClick={isMobileMenuRow ? undefined : onClick}
            onPointerDown={isMobileMenuRow ? onPointerDown : undefined}
            onPointerUp={isMobileMenuRow ? onPointerUp : undefined}
            onPointerCancel={isMobileMenuRow ? onPointerCancel : undefined}
            onKeyDown={isMobileMenuRow ? onKeyActivate : undefined}
            onPointerEnter={disableHover ? undefined : onPointerEnter}
            onPointerLeave={disableHover ? undefined : onPointerLeave}
        >
            <motion.div
                className={styles.background}
                aria-hidden
                initial={{ scaleX: 0 }}
                animate={bgControls}
                style={{ transformOrigin: 'left center' }}
            />
            {isMobileMenuRow ? (
                <>
                    <motion.div
                        className={styles.pressHighlightBar}
                        aria-hidden
                        initial={{ scaleX: 0 }}
                        animate={pressHighlightControls}
                        style={{ transformOrigin: 'left center' }}
                    />
                    <motion.div
                        className={styles.wipeOverBar}
                        aria-hidden
                        initial={{ scaleX: 0 }}
                        animate={wipeOverControls}
                        style={{ transformOrigin: 'left center' }}
                    />
                </>
            ) : null}
            <motion.span
                className={`${styles.indicator} ${isSelected ? styles.indicatorVisible : ''}`}
                aria-hidden
            />
            <motion.span
                className={`${styles.text} ${textPressWhite ? styles.textPressWhite : ''}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: textOpacity }}
                transition={{ duration: 0 }}
            >
                {title}
            </motion.span>
        </div>
    );
};

export default MenuItem;
